// Vuetify
import 'vuetify/styles'
// Styles
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import './styles/main.scss'
import 'viewerjs/dist/viewer.css'

import { createSentryPiniaPlugin } from '@sentry/vue'
import { createPinia } from 'pinia'
import VueViewer from 'v-viewer'
import { registerSW } from 'virtual:pwa-register'
import { createApp } from 'vue'

// i18n configuration
import { i18n, t } from '@/i18n'
// Services
import { setupApiInterceptors } from '@/services/apiClient'
import { createAuthSessionManager } from '@/services/authSessionManager'
import { notificationService } from '@/services/notificationService'
import { isSentryEnabled } from '@/services/telemetry/sentryEnv'
// Stores
import { useCommonStore, useDashboardStore, useUserStore } from '@/stores'

import App from './App.vue'
import vuetify from './plugins/vuetify'
import router from './router'
// Utils
import { setAuthToken, setUpApiConfig } from './utils/initializeApi'
import { initSentry } from './utils/sentry'

const app = createApp(App)

if (isSentryEnabled()) {
    initSentry({ app, router })
}

const pinia = createPinia()

if (isSentryEnabled()) {
    pinia.use(
        createSentryPiniaPlugin({
            attachPiniaState: false,
            addBreadcrumbs: true,
            stateTransformer: () => undefined
        })
    )
}

app.use(pinia)

// Initialize stores
useUserStore()
useCommonStore()
useDashboardStore()

app.use(router)
app.use(vuetify)
app.use(i18n)
app.use(VueViewer)

setUpApiConfig()

// Initialize the auth header if a token exists from a previous session
const existingToken = localStorage.getItem('token')
const receivedPermissions = localStorage.getItem('receivedPermissions')
const hasAuthenticatedSession = receivedPermissions ? JSON.parse(receivedPermissions) === true : false
if (existingToken && hasAuthenticatedSession) {
    setAuthToken(existingToken)
}

const userStore = useUserStore()
const commonStore = useCommonStore()

if (userStore.token && !userStore.permissionsReceived) {
    userStore.logout()
}

notificationService.subscribe((notification) => {
    commonStore.showNotification(notification)
})

const authSessionManager = createAuthSessionManager({
    userStore,
    commonStore,
    router
})

setupApiInterceptors({
    onUnauthorized: (error) => {
        authSessionManager.handleUnauthorized(error)
    },
    onNetworkIssue: () => {
        notificationService.error(t('common.auth.connectionError'))
    },
    onTokenCheck: () => {
        void authSessionManager.inspectToken().catch(() => {})
    }
})

app.mount('#app')

const updateSW = registerSW({
    onNeedRefresh() {
        if (userStore.token) {
            notificationService.info(t('common.app.updateAvailableUpdating'), 3000)
            updateSW(true).catch(() => {
                notificationService.error(t('common.app.updateFailed'), 5000)
            })
        } else {
            commonStore.setAppUpdateState(true)
            notificationService.info(t('common.app.updateAvailableOnLogin'), 5000)
        }
    },
    onOfflineReady() {
        notificationService.success(t('common.app.offlineReady'), 3000)
    }
})
